import * as Yup from 'yup'
import {
  amountGreaterThanZeroMsg,
  awsAccountIdFieldValidationMessage,
  awsRegexValidationMessage,
  duplicateDateMessage,
  emptySpacesMessage,
  enterAmountMessage,
  enterNumberMessage,
  enterNumberMsg,
  enterQuantityMsg,
  mandatoryFieldMessage,
  missignFieldMessage,
  onePaymentBeforeServiceDateMsg,
  paymentDateValidationMessage,
  quantityDecimalMsg,
} from './messagesContants'
import {
  awsAccountIdRegex,
  emptySpaceCharsAtStartAndEndRegex,
  initialSpaceRegex,
} from './constants'
import { validateEmptyValueAndNewLines } from './validateEmptyValueAndNewLines'
import _, { filter, get, isEmpty, isNil } from 'lodash'
import { PlanType } from '@labrav/react-components'
import { DateTime } from 'luxon'
import { CppoPriceAdjustmentType } from '../../modules/channelPartnerPrivateOffer/types'
import { DiscountType, type Installments } from '../../types/flyout'
import { getMarkupValues } from '../../utils/channelOfferUtil'
import { isNonEmptyNumber } from '@labrav/common'
import type { ReceivedResellerAuthorization } from '../../modules/receivedResellerAuthorization/types'
import './globalValidations'

type CustomStakeholder = {
  email: string
}

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
    installmentsValidation(
      propertyPath: string,
      message?: string
    ): ArraySchema<T>
  }
}

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = get(item, propertyPath)

      if (
        propertyValue &&
        filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

Yup.addMethod(
  Yup.array,
  'installmentsValidation',
  function (propertyPath, timeZone, message) {
    return this.test('installmentsValidation', '', function (list: any) {
      const errors: any = []
      const offerExpirationDate = this.options?.context?.offerExpirationDate
      const paymentDatesBeforeExpirationDate = _.filter(
        list,
        (item: Installments) =>
          DateTime.fromISO(item.paymentDate, { zone: timeZone }) <=
          DateTime.fromISO(offerExpirationDate, { zone: timeZone })
      ).map(item => item.paymentDate)

      list?.forEach((item: any, index: number) => {
        if (
          paymentDatesBeforeExpirationDate.length >= 2 &&
          paymentDatesBeforeExpirationDate.includes(item.paymentDate)
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
    installmentsValidation(
      propertyPath: string,
      message?: string
    ): ArraySchema<T>
  }
}

declare module 'yup' {
  interface NumberSchema {
    usageDurationValidation({
      propertyPath,
    }: {
      propertyPath: string
    }): NumberSchema
  }
}

const metaDataItemValidationSchema = Yup.object().shape({
  key: Yup.string(),
  value: Yup.string(),
})

metaDataItemValidationSchema.fields.key =
  metaDataItemValidationSchema.fields.key.when('value', {
    is: (value: string) => value && value.trim().length > 0,
    then: Yup.string()
      .required(missignFieldMessage)
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
    otherwise: Yup.string().matches(
      emptySpaceCharsAtStartAndEndRegex,
      `${mandatoryFieldMessage}.`
    ),
  })

metaDataItemValidationSchema.fields.value =
  metaDataItemValidationSchema.fields.value.when('key', {
    is: (key: string) => key && key.trim().length > 0,
    then: Yup.string()
      .required(missignFieldMessage)
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
    otherwise: Yup.string().matches(
      emptySpaceCharsAtStartAndEndRegex,
      `${mandatoryFieldMessage}.`
    ),
  })

const offerDetailValidations = Yup.object().shape({
  companyName: Yup.string()
    .required('Buyer name cannot be empty.')
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
  customEmailMessage: Yup.string()
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
    .test(
      'contains-new-line-characters',
      `${emptySpacesMessage}`,
      validateEmptyValueAndNewLines
    ),
  offerName: Yup.string()
    .required('Offer name cannot be empty.')
    .test(
      'contains-specific-characters',
      'Offer name cannot contain special characters ("","<" or ">") or spaces at start or end.',
      (value: string | undefined) => {
        if (!value) {
          return true
        }
        return !(
          value.includes('\\') ||
          value.includes('>') ||
          value.includes('<') ||
          !new RegExp(initialSpaceRegex).test(value)
        )
      }
    )
    .max(150, 'Please provide offer name with fewer than 150 characters.')
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
  offerExpirationDate: Yup.string()
    .nullable()
    .required('Please provide the offer expiration date.'),
  offerDescription: Yup.string()
    .nullable()
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
    .max(255, 'Provide offer description with fewer than 255 characters.'),
  offerRecipients: Yup.array()
    .of(
      Yup.object().shape({
        firstName: Yup.string()
          .required(missignFieldMessage)
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          ),
        lastName: Yup.string()
          .required(missignFieldMessage)
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          ),
        title: Yup.string()
          .nullable()
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          ),
        email: Yup.string()
          .required('Please provide the email of your offer recipient. ')
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          )
          .email('Enter valid email.'),
      })
    )
    .uniqueProperty('email', 'Duplicate emails are not allowed.'),
  offerStakeholders: Yup.array()
    .of(
      Yup.object().shape({
        email: Yup.string().required('Email cannot be empty.'),
      })
    )
    .min(1, 'At least one stakeholder is required.'),
  customOfferStakeholders: Yup.array().test(
    'Enter-valid-emails',
    'Enter valid emails',
    function (value?: CustomStakeholder[]) {
      if (!value) return true
      return value?.every(v =>
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          v.email
        )
      )
    }
  ),
  awsAccountId: Yup.string()
    .required(mandatoryFieldMessage)
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
    .min(12, awsAccountIdFieldValidationMessage)
    .max(12, awsAccountIdFieldValidationMessage)
    .matches(awsAccountIdRegex, awsRegexValidationMessage),
  metaData: Yup.array().of(metaDataItemValidationSchema),
  eulaFile: Yup.array().optional().notRequired(),
})

const {
  awsAccountId,
  companyName,
  customOfferStakeholders,
  customEmailMessage,
  offerDescription,
  offerExpirationDate,
  offerName,
  offerRecipients,
  offerStakeholders,
  eulaFile,
  metaData,
} = offerDetailValidations.fields

export function generateChannelPartnerPrivateOfferValidationSchema(
  timeZone: string,
  planType?: PlanType,
  resellerLicenseAgreementFiles?: string[],
  resellerAuthDiscountType?: DiscountType,
  resaleAuthorization?: ReceivedResellerAuthorization
) {
  return Yup.object().shape({
    awsAccountId,
    companyName,
    customOfferStakeholders,
    customEmailMessage,
    offerDescription,
    offerExpirationDate,
    offerName,
    offerRecipients,
    offerStakeholders,
    eulaFile,
    metaData,
    subscriptionEndDate: generateSubscriptionEndDateSchema(planType),
    priceAdjustmentType: Yup.string().required('1 option is Required.'),
    adjustmentPercentageInputValue: Yup.number().when(['priceAdjustmentType'], {
      is: (priceAdjustmentType: CppoPriceAdjustmentType) =>
        priceAdjustmentType === CppoPriceAdjustmentType.FIXED,
      then: Yup.number()
        .checkDecimalpointValidation({
          message: 'Only 2 decimal places allowed.',
        })
        .nullable()
        .test(
          'greater-than-or-equal-zero',
          'value must be valid.',
          function (value?: number | null) {
            return !isNil(value) && value >= 0
          }
        )
        .test(
          'check with total due to ISV',
          'The value must be at least equal to total due to ISV.',
          function (value) {
            if (!isNonEmptyNumber(value || undefined)) return true
            const { totalDueToIsv } = getMarkupValues({
              dimensions: this.options?.context?.dimensions,
              priceAdjustmentType: this.options?.context?.priceAdjustmentType,
              resaleAuthorization,
            })
            return !isNil(value) && value >= (totalDueToIsv || 0)
          }
        ),
      otherwise: Yup.number()
        .nullable()
        .test(
          'greater-than-or-equal-zero',
          'value must be valid.',
          function (value?: number | null) {
            return !isNil(value) && value >= 0
          }
        )
        .required('Please enter a number'),
    }),
    isResellerLicenseAgreementAccepted: resellerLicenseAgreementFiles?.length
      ? Yup.bool()
          .required('You must accept the reseller agreement.')
          .oneOf([true], 'You must accept the reseller agreement.')
      : Yup.bool().nullable(),
    variableStartDate: Yup.boolean().nullable(),
    agreementStartDate: Yup.string().when('variableStartDate', {
      is: false,
      then: Yup.string()
        .nullable()
        .required('Please provide the start date.')
        .test(
          'check speciffied date',
          'Specified date cannot be less than offer expiry date',
          function (value?: string | null) {
            const offerExpirationDate =
              this.options?.context?.offerExpirationDate
            if (offerExpirationDate) {
              return (
                DateTime.fromISO(offerExpirationDate) <=
                DateTime.fromISO(value || '')
              )
            }
            return true
          }
        ),
      otherwise: Yup.string().nullable(),
    }),
    serviceLength: Yup.string()
      .nullable()
      .when(['variableStartDate', 'flexiblePaymentTerms'], {
        is: (variableStartDate?: boolean, flexiblePaymentTerms?: boolean) =>
          planType === PlanType.SAAS_SUBSCRIPTION ||
          (resellerAuthDiscountType !== DiscountType.FLEXIBLE_PAYMENT_TERMS &&
            variableStartDate &&
            !flexiblePaymentTerms),
        then: Yup.string().nullable(),
        otherwise: Yup.string()
          .nullable()
          .required('You must select one contract duration.'),
      }),
    installments: Yup.array().when('flexiblePaymentTerms', {
      is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
      then:
        resellerAuthDiscountType !== DiscountType.FLEXIBLE_PAYMENT_TERMS
          ? Yup.array()
              .of(
                Yup.object().shape({
                  paymentDate: Yup.string()
                    .nullable()
                    .required('Payment date is required')
                    .test(
                      'check contract duration date',
                      paymentDateValidationMessage,
                      function (value?: string | null) {
                        let duration = this.options?.context?.serviceLength
                        if (!duration) {
                          duration = '0'
                        }
                        const currentDate = this.options?.context
                          ?.agreementStartDate
                          ? DateTime.fromISO(
                              this.options?.context?.agreementStartDate
                            )
                          : DateTime.now()
                        const serviceEndDate = currentDate.plus({
                          months: parseInt(duration),
                        })

                        const serviceEndDateConverted = serviceEndDate
                          ? serviceEndDate.toISODate()
                          : null
                        if (
                          !isEmpty(value) &&
                          !isEmpty(serviceEndDateConverted)
                        ) {
                          return (
                            DateTime.fromISO(serviceEndDateConverted || '') >=
                            DateTime.fromISO(value || '')
                          )
                        }

                        return true
                      }
                    ),
                  otherAmount: Yup.number(),
                  amount: Yup.number()
                    .typeError(enterNumberMessage)
                    .required(enterAmountMessage)
                    .test(
                      'check amount validation',
                      amountGreaterThanZeroMsg,
                      function (value?: number) {
                        return !isNil(value) && value >= 0
                      }
                    )
                    .checkDecimalpointValidation({
                      message: 'Only 2 decimal places allowed.',
                    }),
                })
              )
              .uniqueProperty('paymentDate', duplicateDateMessage)
              .installmentsValidation(
                'paymentDate',
                timeZone,
                onePaymentBeforeServiceDateMsg
              )
          : Yup.array().of(
              Yup.object().shape({
                otherAmount: Yup.number(),
                amount: Yup.number().when('priceAdjustmentType', {
                  is: (priceAdjustmentType: CppoPriceAdjustmentType) =>
                    priceAdjustmentType === CppoPriceAdjustmentType.FIXED,
                  then: Yup.number()
                    .typeError(enterNumberMessage)
                    .required(enterAmountMessage)
                    .test(
                      'check amount validation',
                      amountGreaterThanZeroMsg,
                      function (value?: number) {
                        return !isNil(value) && value >= 0
                      }
                    )
                    .when('otherAmount', {
                      is: (value?: number) =>
                        isNonEmptyNumber(value) &&
                        resellerAuthDiscountType !==
                          DiscountType.FLEXIBLE_PAYMENT_TERMS,
                      then: Yup.number().min(
                        Yup.ref('otherAmount'),
                        'The value must be at least equal to payment to ISV'
                      ),
                    })
                    .checkDecimalpointValidation({
                      message: 'Only 2 decimal places allowed.',
                    }),
                }),
              })
            ),
    }),
    dimensions: Yup.array().when('flexiblePaymentTerms', {
      is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
      then: Yup.array().of(
        Yup.object().shape({
          isAdditionalUsageAllowed: Yup.boolean(),
          quantity: Yup.number().when('isAdditionalUsageAllowed', {
            is: (value: boolean) => !value,
            then: Yup.number()
              .nullable()
              .typeError(enterNumberMsg)
              .required(enterQuantityMsg)
              .integer(quantityDecimalMsg)
              .test(
                'check negative value',
                'Please enter valid quantity.',
                function (value) {
                  return !isNil(value) && value >= 0
                }
              ),
            otherwise: Yup.number().nullable(),
          }),
        })
      ),
    }),
  })
}

export function generateABOChannelOfferValidationSchema(
  timeZone: string,
  planType?: PlanType,
  resellerLicenseAgreementFiles?: string[],
  resellerAuthDiscountType?: DiscountType,
  resaleAuthorization?: ReceivedResellerAuthorization
) {
  return Yup.object().shape({
    awsAccountId,
    companyName,
    customOfferStakeholders,
    customEmailMessage,
    offerDescription,
    offerExpirationDate,
    offerName,
    offerRecipients,
    offerStakeholders,
    eulaFile,
    metaData,
    subscriptionEndDate: generateSubscriptionEndDateSchema(planType),
    priceAdjustmentType: Yup.string().required('1 option is Required.'),
    adjustmentPercentageInputValue: Yup.number().when(['priceAdjustmentType'], {
      is: (priceAdjustmentType: CppoPriceAdjustmentType) =>
        priceAdjustmentType === CppoPriceAdjustmentType.FIXED,
      then: Yup.number()
        .checkDecimalpointValidation({
          message: 'Only 2 decimal places allowed.',
        })
        .nullable()
        .test(
          'greater-than-or-equal-zero',
          'value must be valid.',
          function (value?: number | null) {
            return !isNil(value) && value >= 0
          }
        )
        .test(
          'check with total due to ISV',
          'The value must be at least equal to total due to ISV.',
          function (value) {
            if (!isNonEmptyNumber(value || undefined)) return true
            const { totalDueToIsv } = getMarkupValues({
              dimensions: this.options?.context?.dimensions,
              priceAdjustmentType: this.options?.context?.priceAdjustmentType,
              resaleAuthorization,
            })
            return !isNil(value) && value >= (totalDueToIsv || 0)
          }
        ),
      otherwise: Yup.number()
        .nullable()
        .test(
          'greater-than-or-equal-zero',
          'value must be valid.',
          function (value?: number | null) {
            return !isNil(value) && value >= 0
          }
        )
        .required('Please enter a number'),
    }),
    isResellerLicenseAgreementAccepted: resellerLicenseAgreementFiles?.length
      ? Yup.bool()
          .required('You must accept the reseller agreement.')
          .oneOf([true], 'You must accept the reseller agreement.')
      : Yup.bool().nullable(),
    serviceLength: Yup.string()
      .nullable()
      .when(['variableStartDate', 'flexiblePaymentTerms'], {
        is: (variableStartDate?: boolean, flexiblePaymentTerms?: boolean) =>
          planType === PlanType.SAAS_SUBSCRIPTION ||
          (resellerAuthDiscountType !== DiscountType.FLEXIBLE_PAYMENT_TERMS &&
            variableStartDate &&
            !flexiblePaymentTerms),
        then: Yup.string().nullable(),
        otherwise: Yup.string()
          .nullable()
          .required('You must select one contract duration.'),
      }),
    installments:
      resellerAuthDiscountType !== DiscountType.FLEXIBLE_PAYMENT_TERMS
        ? Yup.array()
            .of(
              Yup.object().shape({
                paymentDate: Yup.string()
                  .nullable()
                  .required('Payment date is required')
                  .test(
                    'check contract duration date',
                    paymentDateValidationMessage,
                    function (value?: string | null) {
                      let duration = this.options?.context?.serviceLength
                      if (!duration) {
                        duration = '0'
                      }
                      const currentDate = this.options?.context
                        ?.agreementStartDate
                        ? DateTime.fromISO(
                            this.options?.context?.agreementStartDate
                          )
                        : DateTime.now()
                      const serviceEndDate = currentDate.plus({
                        months: parseInt(duration),
                      })

                      const serviceEndDateConverted = serviceEndDate
                        ? serviceEndDate.toISODate()
                        : null
                      if (
                        !isEmpty(value) &&
                        !isEmpty(serviceEndDateConverted)
                      ) {
                        return (
                          DateTime.fromISO(serviceEndDateConverted || '') >=
                          DateTime.fromISO(value || '')
                        )
                      }

                      return true
                    }
                  ),
                otherAmount: Yup.number(),
                amount: Yup.number()
                  .typeError(enterNumberMessage)
                  .required(enterAmountMessage)
                  .test(
                    'check amount validation',
                    amountGreaterThanZeroMsg,
                    function (value?: number) {
                      return !isNil(value) && value >= 0
                    }
                  )
                  .when('otherAmount', {
                    is: (value?: number) => isNonEmptyNumber(value),
                    then: Yup.number().min(
                      Yup.ref('otherAmount'),
                      'The value must be at least equal to payment to ISV'
                    ),
                  })
                  .checkDecimalpointValidation({
                    message: 'Only 2 decimal places allowed.',
                  }),
              })
            )
            .uniqueProperty('paymentDate', duplicateDateMessage)
            .installmentsValidation(
              'paymentDate',
              timeZone,
              onePaymentBeforeServiceDateMsg
            )
        : Yup.array().of(
            Yup.object().shape({
              otherAmount: Yup.number(),
              amount: Yup.number()
                .typeError(enterNumberMessage)
                .required(enterAmountMessage)
                .test(
                  'check amount validation',
                  amountGreaterThanZeroMsg,
                  function (value?: number) {
                    return !isNil(value) && value >= 0
                  }
                )
                .when(['otherAmount', 'priceAdjustmentType'], {
                  is: (
                    value?: number,
                    priceAdjustmentType?: CppoPriceAdjustmentType
                  ) =>
                    isNonEmptyNumber(value) &&
                    priceAdjustmentType === CppoPriceAdjustmentType.FIXED,
                  then: Yup.number().min(
                    Yup.ref('otherAmount'),
                    'The value must be at least equal to payment to ISV'
                  ),
                })
                .checkDecimalpointValidation({
                  message: 'Only 2 decimal places allowed.',
                }),
            })
          ),
    dimensions: Yup.array().when('flexiblePaymentTerms', {
      is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
      then: Yup.array().of(
        Yup.object().shape({
          isAdditionalUsageAllowed: Yup.boolean(),
          quantity: Yup.number().when('isAdditionalUsageAllowed', {
            is: (value: boolean) => !value,
            then: Yup.number()
              .nullable()
              .typeError(enterNumberMsg)
              .required(enterQuantityMsg)
              .integer(quantityDecimalMsg)
              .test(
                'check negative value',
                'Please enter valid quantity.',
                function (value) {
                  return !isNil(value) && value >= 0
                }
              ),
            otherwise: Yup.number().nullable(),
          }),
        })
      ),
    }),
  })
}

export function generateSubscriptionEndDateSchema(
  planType: PlanType | undefined
) {
  if (planType === PlanType.SAAS_SUBSCRIPTION) {
    return Yup.string()
      .nullable()
      .required('Usage end date is required')
      .test(
        'check Usage end date',
        'Usage end date should be greater than offer expiration date',
        function (value?: string | null) {
          const offerExpiryDate = this.options?.context?.offerExpirationDate
          if (!isEmpty(value) && !isEmpty(offerExpiryDate)) {
            return (
              DateTime.fromISO(offerExpiryDate) < DateTime.fromISO(value || '')
            )
          }
          return true
        }
      )
  } else {
    return Yup.string().nullable()
  }
}
